import React, { useEffect, useMemo, useRef, useState } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Skeleton, Spinner } from "../../Components/Common/LoadingStates";
import {
  getAppErrors,
  getAppErrorsSummary,
  getAppErrorById,
  patchAppErrorResolve,
  postAppErrorsSync,
} from "../../helpers/fakebackend_helper";
import { useCachedRequest, invalidateCache } from "../../helpers/cachedFetch";

document.title = "App Errors | BiorGov Admin";

const PAGE_SIZE = 25;

const PLATFORMS = [
  { value: "", label: "All platforms" },
  { value: "ios", label: "iOS" },
  { value: "android", label: "Android" },
];

const RESOLVED_FILTERS = [
  { value: "false", label: "Unresolved" },
  { value: "true", label: "Resolved" },
  { value: "all", label: "All" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return String(iso);
  }
}

function truncate(s, n = 110) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

function SummaryCard({ label, value, accent = "var(--bl-accent)", loading }) {
  const accentRgb = accent.includes("danger") || accent.includes("error")
    ? "255,110,110"
    : accent.includes("warn")
    ? "243,175,76"
    : accent.includes("success")
    ? "0,205,82"
    : "123,124,255";
  return (
    <div className="bl-glass" style={{ padding: "1.1rem 1.25rem" }}>
      <div style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      {loading ? (
        <div style={{ marginTop: 8 }}><Skeleton height={28} width="50%" /></div>
      ) : (
        <div style={{
          marginTop: 4,
          fontSize: "1.75rem",
          fontFamily: "var(--bl-font-numeric)",
          fontWeight: 700,
          color: `rgb(${accentRgb})`,
          fontVariantNumeric: "tabular-nums",
        }}>{Number(value || 0).toLocaleString()}</div>
      )}
    </div>
  );
}

function PlatformBadge({ platform }) {
  const p = (platform || "").toLowerCase();
  let bg = "rgba(255,255,255,0.06)";
  let color = "var(--bl-text-soft)";
  let label = platform || "unknown";
  if (p === "ios") { bg = "rgba(123,124,255,0.18)"; color = "#a6a7ff"; label = "iOS"; }
  else if (p === "android") { bg = "rgba(0,205,82,0.18)"; color = "#5be58a"; label = "Android"; }
  return (
    <span style={{
      fontSize: 11, padding: "2px 8px", borderRadius: 6, background: bg, color,
      fontWeight: 600, letterSpacing: "0.02em", whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

function ResolvedBadge({ resolved }) {
  const bg = resolved ? "rgba(0,205,82,0.16)" : "rgba(243,175,76,0.16)";
  const color = resolved ? "#5be58a" : "#f3af4c";
  return (
    <span style={{
      fontSize: 11, padding: "2px 8px", borderRadius: 6, background: bg, color,
      fontWeight: 600, letterSpacing: "0.02em", whiteSpace: "nowrap",
    }}>{resolved ? "Resolved" : "Open"}</span>
  );
}

const AppErrors = ({ embedded = false } = {}) => {
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [platform, setPlatform] = useState("");
  const [resolved, setResolved] = useState("false");

  // Detail drawer
  const [selected, setSelected] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [resolving, setResolving] = useState(false);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [debouncedSearch, platform, resolved]);

  const rowsReq = useCachedRequest({
    key: `app-errors:list:${page}:${resolved}:${platform}:${debouncedSearch}`,
    fetcher: () => {
      const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, resolved };
      if (platform) params.platform = platform;
      if (debouncedSearch) params.search = debouncedSearch;
      // Interceptor already unwrapped the HTTP body; fields are spread at the
      // top level: { status, message, total, limit, offset, rows }.
      return getAppErrors(params).then((res) => res || {});
    },
    deps: [page, resolved, platform, debouncedSearch],
  });
  const rows = Array.isArray(rowsReq.data?.rows) ? rowsReq.data.rows : [];
  const total = Number.isFinite(rowsReq.data?.total) ? rowsReq.data.total : 0;
  const loading = rowsReq.loading;

  const summaryReq = useCachedRequest({
    key: "app-errors:summary",
    fetcher: () => getAppErrorsSummary().then((res) => res || {}).catch(() => null),
  });
  const summary = summaryReq.data;
  const summaryLoading = summaryReq.loading;

  useEffect(() => {
    if (rowsReq.error && !rowsReq.data) {
      setError(typeof rowsReq.error === "string" ? rowsReq.error : rowsReq.error?.message || "Failed to load errors");
    }
  }, [rowsReq.error, rowsReq.data]);

  const handleOpen = async (row) => {
    setSelected({ ...row });
    setSelectedLoading(true);
    try {
      const res = await getAppErrorById(row.id);
      const body = res || row;
      setSelected(body);
    } catch (_) {
      // Keep the row data we already have if detailed fetch fails
    } finally {
      setSelectedLoading(false);
    }
  };

  const handleResolve = async (nextResolved) => {
    if (!selected) return;
    setResolving(true);
    try {
      const res = await patchAppErrorResolve(selected.id, nextResolved);
      const body = res;
      setSelected(body);
      // Mutation: the row may now disappear from the filtered view. Drop
      // cached list views so the next visit picks up the change.
      invalidateCache("app-errors:");
      rowsReq.refetch();
      summaryReq.refetch();
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "Failed to update");
    } finally {
      setResolving(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    setError("");
    try {
      await postAppErrorsSync();
      invalidateCache("app-errors:");
      await Promise.all([rowsReq.refetch(), summaryReq.refetch()]);
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const counts = summary?.counts || {};

  const topVersions = useMemo(() => {
    const list = summary?.top_versions_30d || [];
    return list.filter((v) => v.app_version).slice(0, 5);
  }, [summary]);

  const byPlatform = useMemo(() => {
    const list = summary?.by_platform_30d || [];
    return list
      .map((p) => ({ platform: p.platform || "unknown", count: Number(p.count) || 0 }))
      .filter((p) => p.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [summary]);

  // When rendered as a System Health sub-tab (embedded), drop the page chrome
  // (breadcrumb + outer padding) so it doesn't double-pad inside the tab body.
  const Wrapper = embedded ? React.Fragment : "div";
  const wrapperProps = embedded
    ? {}
    : { style: { padding: "clamp(1rem, 2.5vw, 1.75rem)", maxWidth: 1400, margin: "0 auto" } };

  return (
    <Wrapper {...wrapperProps}>
      {!embedded && <BreadCrumb title="App Errors" pageTitle="Dashboard" />}

      {error && (
        <div style={{
          marginBottom: "1rem", padding: "0.75rem 1rem", borderRadius: 8,
          background: "rgba(255,110,110,0.1)", border: "1px solid rgba(255,110,110,0.3)",
          color: "#ff9e9e", fontSize: 13,
        }}>{error}</div>
      )}

      {/* ── Summary row ───────────────────────────────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: "1rem",
        marginBottom: "1.25rem",
      }}>
        <SummaryCard label="Last 24h"   value={counts.last_24h}   accent="var(--bl-danger)"  loading={summaryLoading} />
        <SummaryCard label="Last 7 days" value={counts.last_7d}   accent="var(--bl-warn)"    loading={summaryLoading} />
        <SummaryCard label="Last 30 days" value={counts.last_30d} accent="var(--bl-accent)"  loading={summaryLoading} />
        <SummaryCard label="Unresolved (all time)" value={counts.unresolved} accent="var(--bl-warn)"    loading={summaryLoading} />
      </div>

      {/* ── Filter bar ────────────────────────────────────────────────── */}
      <div className="bl-glass" style={{ padding: "1rem", marginBottom: "1rem", display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center" }}>
        <input
          type="text"
          placeholder="Search message, error, stack…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: "1 1 240px", minWidth: 200,
            padding: "0.55rem 0.85rem", borderRadius: 8,
            background: "rgba(255,255,255,0.04)", border: "1px solid var(--bl-line)",
            color: "#fff", fontSize: 13, outline: "none",
          }}
        />
        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          style={{
            padding: "0.55rem 0.85rem", borderRadius: 8,
            background: "rgba(255,255,255,0.04)", border: "1px solid var(--bl-line)",
            color: "#fff", fontSize: 13, outline: "none", minWidth: 140,
          }}
        >
          {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <div role="tablist" aria-label="Resolved filter" style={{
          display: "inline-flex", background: "rgba(255,255,255,0.04)",
          border: "1px solid var(--bl-line)", borderRadius: 8, padding: 2,
        }}>
          {RESOLVED_FILTERS.map((f) => {
            const active = f.value === resolved;
            return (
              <button
                key={f.value}
                role="tab"
                aria-selected={active}
                onClick={() => setResolved(f.value)}
                style={{
                  border: "none",
                  background: active ? "var(--bl-accent)" : "transparent",
                  color: active ? "#fff" : "var(--bl-text-soft)",
                  fontSize: 12, fontWeight: 600,
                  padding: "6px 12px", borderRadius: 6,
                  cursor: "pointer", transition: "background 120ms ease, color 120ms ease",
                }}
              >{f.label}</button>
            );
          })}
        </div>
        <button
          onClick={handleSyncNow}
          disabled={syncing}
          style={{
            marginLeft: "auto",
            padding: "0.55rem 0.85rem", borderRadius: 8,
            background: "rgba(123,124,255,0.16)", border: "1px solid rgba(123,124,255,0.3)",
            color: "#a6a7ff", fontSize: 12, fontWeight: 600,
            cursor: syncing ? "not-allowed" : "pointer", opacity: syncing ? 0.6 : 1,
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
          title="Pull the latest events from PostHog now"
        >
          {syncing ? <Spinner size={12} /> : <i className="ri-refresh-line" />}
          {syncing ? "Syncing…" : "Sync now"}
        </button>
      </div>

      {/* ── Platform breakdown strip (30d) ────────────────────────────── */}
      {byPlatform.length > 0 && (
        <div style={{ marginBottom: "1rem", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            By platform (30d):
          </span>
          {byPlatform.map((p) => (
            <span key={p.platform} style={{
              fontSize: 11, padding: "2px 8px", borderRadius: 6,
              background: "rgba(255,255,255,0.04)", border: "1px solid var(--bl-line)",
              color: "var(--bl-text-soft)", textTransform: "capitalize",
            }}>
              {p.platform} · {p.count.toLocaleString()}
            </span>
          ))}
        </div>
      )}

      {/* ── Top versions strip (optional context) ─────────────────────── */}
      {topVersions.length > 0 && (
        <div style={{ marginBottom: "1rem", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Top versions (30d):
          </span>
          {topVersions.map((v) => (
            <span key={v.app_version} style={{
              fontSize: 11, padding: "2px 8px", borderRadius: 6,
              background: "rgba(255,255,255,0.04)", border: "1px solid var(--bl-line)",
              color: "var(--bl-text-soft)",
            }}>
              v{v.app_version} · {Number(v.count).toLocaleString()}
            </span>
          ))}
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────────────── */}
      <div className="bl-glass" style={{ overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                <th style={th}>Occurred</th>
                <th style={th}>Platform</th>
                <th style={th}>Version</th>
                <th style={th}>Message</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td colSpan={5} style={{ padding: "0.75rem 1rem" }}>
                      <Skeleton height={18} width="100%" />
                    </td>
                  </tr>
                ))
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: "2rem 1rem", textAlign: "center", color: "var(--bl-text-soft)" }}>
                    No errors found with the current filters.
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => handleOpen(row)}
                  style={{
                    borderTop: "1px solid var(--bl-line)",
                    cursor: "pointer",
                    transition: "background 120ms ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.03)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={td}>
                    <div style={{ color: "#fff", fontVariantNumeric: "tabular-nums" }}>{fmtDate(row.occurred_at)}</div>
                  </td>
                  <td style={td}><PlatformBadge platform={row.platform} /></td>
                  <td style={td}>{row.app_version ? `v${row.app_version}` : <span style={{ color: "var(--bl-text-soft)" }}>—</span>}</td>
                  <td style={{ ...td, maxWidth: 480 }}>
                    <div style={{ color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {row.message || row.error_text || "—"}
                    </div>
                  </td>
                  <td style={td}><ResolvedBadge resolved={row.resolved} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ─────────────────────────────────────────────── */}
        <div style={{
          padding: "0.75rem 1rem", borderTop: "1px solid var(--bl-line)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontSize: 12, color: "var(--bl-text-soft)",
        }}>
          <span>
            {total === 0 ? "0 errors" : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total.toLocaleString()}`}
            {loading && rows.length > 0 && <Spinner size={11} style={{ marginLeft: 8, verticalAlign: "middle" }} />}
          </span>
          <div style={{ display: "inline-flex", gap: 6 }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              style={pagerBtn(page <= 1 || loading)}
            >Prev</button>
            <span style={{ padding: "5px 10px", color: "var(--bl-text-soft)" }}>{page} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              style={pagerBtn(page >= totalPages || loading)}
            >Next</button>
          </div>
        </div>
      </div>

      {/* ── Detail drawer ─────────────────────────────────────────────── */}
      {selected && (
        <ErrorDetailDrawer
          row={selected}
          loading={selectedLoading}
          resolving={resolving}
          onClose={() => setSelected(null)}
          onToggleResolve={() => handleResolve(!selected.resolved)}
        />
      )}
    </Wrapper>
  );
};

const th = {
  textAlign: "left",
  padding: "0.65rem 1rem",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--bl-text-soft)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
};

const td = { padding: "0.7rem 1rem", verticalAlign: "middle" };

const pagerBtn = (disabled) => ({
  padding: "5px 10px",
  borderRadius: 6,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid var(--bl-line)",
  color: disabled ? "var(--bl-text-soft)" : "#fff",
  fontSize: 12, fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
});

function ErrorDetailDrawer({ row, loading, resolving, onClose, onToggleResolve }) {
  // Close on ESC for a small UX win
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.55)",
      display: "flex", justifyContent: "flex-end",
      zIndex: 1000,
    }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(640px, 100%)",
          height: "100%",
          background: "var(--bl-panel, #15172b)",
          borderLeft: "1px solid var(--bl-line)",
          padding: "1.25rem",
          overflowY: "auto",
          boxShadow: "-12px 0 32px rgba(0,0,0,0.4)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 style={{ margin: 0, color: "#fff", fontFamily: "var(--bl-font-display)", fontSize: "1.05rem" }}>Error detail</h3>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", color: "var(--bl-text-soft)",
            fontSize: 20, cursor: "pointer",
          }} aria-label="Close">×</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <Detail label="Occurred" value={fmtDate(row.occurred_at)} />
          <Detail label="Platform" value={row.platform || "—"} />
          <Detail label="App version" value={row.app_version ? `v${row.app_version}` : "—"} />
          <Detail label="OS version" value={row.os_version || "—"} />
          <Detail label="Device" value={row.device || "—"} />
          <Detail label="User" value={row.distinct_id || "—"} mono />
        </div>

        <Section title="Message" body={row.message} />
        <Section title="Error" body={row.error_text} />
        <Section title="Stack trace" body={row.stack_trace} mono large />

        <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--bl-line)" }}>
          {row.resolved && row.resolver && (
            <div style={{ marginBottom: 12, fontSize: 12, color: "var(--bl-text-soft)" }}>
              Resolved by <strong style={{ color: "#fff" }}>{row.resolver.name || row.resolver.email}</strong> on {fmtDate(row.resolved_at)}
            </div>
          )}
          <button
            onClick={onToggleResolve}
            disabled={resolving || loading}
            style={{
              padding: "0.6rem 1rem", borderRadius: 8,
              background: row.resolved ? "rgba(243,175,76,0.16)" : "rgba(0,205,82,0.16)",
              border: `1px solid ${row.resolved ? "rgba(243,175,76,0.3)" : "rgba(0,205,82,0.3)"}`,
              color: row.resolved ? "#f3af4c" : "#5be58a",
              fontSize: 13, fontWeight: 600,
              cursor: resolving ? "not-allowed" : "pointer",
              opacity: resolving ? 0.6 : 1,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            {resolving && <Spinner size={12} />}
            {row.resolved ? "Mark as unresolved" : "Mark as resolved"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{
        marginTop: 3, color: "#fff", fontSize: 13,
        fontFamily: mono ? "ui-monospace, SF Mono, Menlo, monospace" : "inherit",
        wordBreak: "break-all",
      }}>{value}</div>
    </div>
  );
}

function Section({ title, body, mono, large }) {
  if (!body) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{title}</div>
      <pre style={{
        margin: 0,
        padding: "0.75rem 0.85rem",
        borderRadius: 8,
        background: "rgba(0,0,0,0.3)",
        border: "1px solid var(--bl-line)",
        color: "#fff",
        fontSize: mono ? 12 : 13,
        fontFamily: mono ? "ui-monospace, SF Mono, Menlo, monospace" : "inherit",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxHeight: large ? 360 : undefined,
        overflowY: large ? "auto" : "visible",
        lineHeight: 1.45,
      }}>{body}</pre>
    </div>
  );
}

export default AppErrors;
